import { Box, Flex, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { AllHtmlEntities } from "html-entities";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { ManagerVerificationStatusEnumType } from "../../../../api/enums";
import {
  getManagerOverviewRoute,
  getOpportunityDetailsRoute,
  managerDashboardRoutes,
} from "../../../../defines";
import { MoreVerticalIcon, MoveIcon } from "../../../../icons";
import { buildRouteUrl, truncateWithEllipses } from "../../../../utils";
import { dateToString } from "../../../../utils/dates";
import { getOpportunityFeaturedImage } from "../../../../utils/opportunities";
import { Button } from "../../Button";
import { ManagerLabel } from "../../managers/ManagerLabel";
import { MenuItem, MenuList, VeriDropMenu } from "../../VeriDropMenu";
import { VerifiedIcon } from "../../VerifiedIcon";
import { VeriHeading } from "../../VeriHeading";
import { OpportunityStatusTag } from "../OpportunityStatusTag";
import { OpportunityCardProps } from "./OpportunityCard.types";

const allHtmlEntities = new AllHtmlEntities();

export const OpportunityCard: React.FC<OpportunityCardProps> = ({
  isRearranging,
  onChangePosition,
  setDraggingIndex,
  index,
  to,
  isEditing,
  isEditor,
  onMoveToTop,
  onMoveToBottom,
  opportunity,
  showManagerTitle,
  showViewManagerButton,
  showViewDetailsButton,
  isNotAButton,
  ...rest
}) => {
  const [isDragOver, setIsDragOver] = React.useState(false);

  const img = getOpportunityFeaturedImage(opportunity);

  let propertyType = "";
  opportunity.attributeitems?.forEach((a) => {
    if (
      a.attributeType === "property_type" &&
      a.isPrimaryPropertyType &&
      a.name
    ) {
      propertyType = a.name;
    }
  });

  if (propertyType === "Not Provided") {
    opportunity.attributeitems?.forEach((a) => {
      if (a.attributeType === "property_type" && a.name) {
        propertyType = a.name;
      }
    });
  }

  function getOpportunityIdLink() {
    if (opportunity?.id) {
      return getOpportunityDetailsRoute(opportunity.id);
    }
    return "";
  }

  function getRedirectPropsTo(to: string) {
    return { as: RouterLink, to };
  }

  function getRedirectToOpportunityLinkProps() {
    if (isRearranging || isNotAButton) {
      return {};
    }

    if (to) {
      return { as: RouterLink, to };
    }

    const linkToPublicOpportunityPage = getOpportunityIdLink();

    if (showViewDetailsButton) {
      return {};
    }

    if (isEditor || isEditing) {
      return {
        as: RouterLink,
        to: buildRouteUrl(managerDashboardRoutes.OPPORTUNITIES_DETAILS, {
          idOpportunity: String(opportunity.id),
        }),
      };
    }

    return { as: RouterLink, to: linkToPublicOpportunityPage };
  }

  const manager = opportunity.manager;
  const managerIsVerified =
    manager?.verificationStatus === ManagerVerificationStatusEnumType.Verified;

  return (
    <>
      {isDragOver && (
        <Box
          id={"opportunity-drop-zone-" + index}
          w={"100%"}
          h={"100%"}
          bg={"border.grey"}
          borderRadius={4}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={() => {
            setIsDragOver(false);
            onChangePosition?.(index);
          }}
        />
      )}
      <SimpleGrid
        id={"opportunity-drop-zone-22-" + index}
        _hover={{
          boxShadow: "6",
        }}
        style={{ transition: "all 800ms cubic-bezier(0.19, 1, 0.22, 1)" }}
        {...(getRedirectToOpportunityLinkProps() as unknown)}
        cursor={isRearranging ? "pointer" : undefined}
        width="100%"
        maxWidth="450px"
        bg="#FFF"
        boxShadow="3"
        borderRadius={4}
        position="relative"
        {...(isRearranging && {
          draggable: true,
          onDragOver: () => setIsDragOver(true),
          onDragStart: () => {
            setDraggingIndex?.(index);
          },
          onDragEnd: () => {
            setIsDragOver(false);
            setDraggingIndex?.(-1);
          },
        })}
        opacity={isDragOver ? "0.2" : undefined}
        direction="column"
        alignContent="space-between"
        h="100%"
        {...rest}
      >
        <Box overflow={"hidden"}>
          {opportunity.status && (
            <Flex position="absolute" top="16px" left="16px">
              <OpportunityStatusTag status={opportunity.status} />
            </Flex>
          )}
          <Box
            mb="-16px"
            width="100%"
            paddingTop="70%"
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundColor="bg.grey"
            borderRadius="4px 4px 0px 0px"
            style={{ backgroundImage: `url('${img}')` }}
          />
          <Box justifyContent="space-between" bg="white" p="16px">
            {propertyType && (
              <VeriHeading
                textStyle="bodyXS"
                fontWeight={600}
                textTransform="uppercase"
                color="text.grey"
                mb="8px"
              >
                {propertyType}
              </VeriHeading>
            )}
            <VeriHeading textStyle="h6" noOfLines={3} textOverflow={"ellipsis"}>
              {opportunity.name}
            </VeriHeading>
            {showManagerTitle && manager && (
              <Flex alignItems="center" gap={1} pt={3} pb={7}>
                <ManagerLabel manager={manager} />
                {managerIsVerified && <VerifiedIcon />}
              </Flex>
            )}
            {opportunity.descriptionHtml && (
              <Box>
                <Text textStyle="bodySmall">
                  {truncateWithEllipses(
                    allHtmlEntities.decode(
                      opportunity.descriptionHtml.replace(/(<([^>]+)>)/gi, ""),
                    ),
                    100,
                  )}
                </Text>
              </Box>
            )}
          </Box>
        </Box>
        <Box p="16px">
          <Box pt="8px">
            <Text color="text.grey" textStyle="bodySmall">
              Inception:{" "}
              {opportunity.inceptionDate
                ? dateToString(new Date(opportunity.inceptionDate))
                : "No date provided"}
            </Text>
          </Box>
          <Stack gap={2} mt={10}>
            {showViewDetailsButton && (
              <Button
                isMedium
                w={"100%"}
                outline
                {...getRedirectPropsTo(getOpportunityIdLink())}
              >
                View details
              </Button>
            )}
            {showViewManagerButton && (
              <Button
                isMedium
                w={"100%"}
                outline
                {...getRedirectPropsTo(getManagerOverviewRoute())}
              >
                View manager overview
              </Button>
            )}
          </Stack>

          {isRearranging && (
            <Flex position={"absolute"} right={0} top={0} p={2}>
              <VeriDropMenu
                placement={"bottom-start"}
                trigger={
                  <Button
                    w={"32px"}
                    h={"32px"}
                    p={0}
                    alignItems={"center"}
                    justifyContent={"center"}
                    isFlat
                  >
                    <MoreVerticalIcon boxSize={"20px"} color={"text.grey"} />
                  </Button>
                }
              >
                <MenuList>
                  <MenuItem onClick={() => onMoveToTop?.(index)}>
                    Move to top
                  </MenuItem>
                  <MenuItem onClick={() => onMoveToBottom?.(index)}>
                    Move to bottom
                  </MenuItem>
                </MenuList>
              </VeriDropMenu>
              <Button
                w={"32px"}
                h={"32px"}
                p={0}
                alignItems={"center"}
                justifyContent={"center"}
                ml={2}
                isFlat
              >
                <MoveIcon boxSize={"20px"} color={"text.grey"} />
              </Button>
            </Flex>
          )}
        </Box>
      </SimpleGrid>
    </>
  );
};
